/**
 * EMA30 Band Breakout
 *
 * Trend-following overlay that wraps price inside a band built from two EMAs —
 * a 30-period EMA of highs and a 30-period EMA of lows — and prints LONG / SHORT
 * labels when price pushes to a new swing beyond its previous one outside that
 * band. A long EMA (200 by default) acts as the trend filter.
 *
 * The band
 *   The space between the EMA30 High and the EMA30 Low is shaded according to
 *   where price closes:
 *     • green  — close above the EMA30 High  (bullish pressure)
 *     • yellow — close inside the band      (neutral / consolidation)
 *     • red    — close below the EMA30 Low   (bearish pressure)
 *
 * LONG signal
 *   While price trades above the EMA30 High the peak of that excursion is
 *   tracked. When price falls back inside the band that peak is stored as the
 *   reference high. A LONG prints when a later push above the band breaks above
 *   that stored peak and the close is above the trend EMA.
 *
 * SHORT signal
 *   The exact mirror: price trades below the EMA30 Low, the lowest point of that
 *   excursion is stored, and a SHORT prints when a later move breaks below that
 *   stored trough with the close below the trend EMA.
 *
 * Staying in the trade
 *   Signals strictly alternate — after a LONG the next signal can only be a
 *   SHORT and vice versa. Turning SHORT signals off disables that rule so LONG
 *   signals can print repeatedly.
 *
 * Reversal signals
 *   Optional catch-all for strong one-directional moves that never break a
 *   previous swing: when the last signal was LONG and price closes below the
 *   EMA30 Low for N consecutive bars a SHORT prints (mirror for the reverse).
 *
 * Reference levels
 *   Small dotted lines mark the stored previous peak and trough so it is obvious
 *   which level a breakout has to clear.
 *
 * Signals are evaluated on closed bars only — the band itself keeps updating on
 * the forming bar, exactly like the original script.
 *
 * Category: Trend (listed under the "Strategies" group) | Pane: Overlay
 */

import {
  IndicatorChartLabel,
  IndicatorCurveBand,
  IndicatorDefinition,
  IndicatorLineSegment,
  PlotData,
} from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeEMA } from "./sma";
import { shade, withAlpha } from "../color-utils";

// ─── Constants ────────────────────────────────────────────────────────────────
const ZONE_BULL = 0;   // close above the band
const ZONE_NEUTRAL = 1; // close inside the band
const ZONE_BEAR = 2;   // close below the band

const SIG_NONE = 0;
const SIG_LONG = 1;
const SIG_SHORT = -1;

/** TradingView's signature signal colours. */
const LONG_COLOR = "#089981";
const SHORT_COLOR = "#F23645";

export const ema30BandBreakoutIndicator: IndicatorDefinition = {
  id: "ema30_band_breakout",
  name: "EMA30 Band Breakout",
  shortName: "EMABB",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "bandLength", label: "EMA Band Length", type: "number", default: 30, min: 2, max: 500, group: "Band" },
    { key: "trendLength", label: "Trend EMA Length", type: "number", default: 200, min: 5, max: 500 },
    { key: "bandFill", label: "Show Band Fill", type: "boolean", default: true },
    { key: "fillOpacity", label: "Fill Opacity %", type: "number", default: 20, min: 5, max: 60, step: 5 },
    { key: "bullColor", label: "Bullish Zone", type: "color", default: "#26A69A" },
    { key: "neutralColor", label: "Neutral Zone", type: "color", default: "#F0B90B" },
    { key: "bearColor", label: "Bearish Zone", type: "color", default: "#EF5350" },

    { key: "useTrendFilter", label: "Use Trend Filter", type: "boolean", default: true, group: "Trend Filter" },
    { key: "showTrendLine", label: "Show Trend EMA", type: "boolean", default: true },

    { key: "confirmByClose", label: "Confirm Breakout By", type: "select", default: "close", group: "Signals", options: [
      { label: "Close", value: "close" },
      { label: "High / Low", value: "hl" },
    ]},
    { key: "showReversals", label: "Show Reversal Signals", type: "boolean", default: true },
    { key: "reversalBars", label: "Reversal Closes", type: "select", default: 2, options: [
      { label: "2", value: 2 }, { label: "3", value: 3 },
      { label: "4", value: 4 }, { label: "5", value: 5 },
    ]},
    { key: "showShort", label: "Show SHORT Signals", type: "boolean", default: true },
    { key: "labelSize", label: "Label Size", type: "number", default: 11, min: 8, max: 18, step: 1 },

    { key: "showLevels", label: "Show Swing Levels", type: "boolean", default: true, group: "Reference Levels" },
    { key: "peakColor", label: "Peak Level", type: "color", default: "#26A69A" },
    { key: "troughColor", label: "Trough Level", type: "color", default: "#EF5350" },

    { key: "showEma50", label: "Show EMA 50", type: "boolean", default: false, group: "EMA50" },
    { key: "ema50Length", label: "Length", type: "number", default: 50, min: 5, max: 500 },
    { key: "ema50Color", label: "Color", type: "color", default: "#2962FF" },
  ],
  plots: [
    // Band edges are stroked by the curve-band primitive (so each edge can pick
    // up its zone colour); these series exist purely to publish the last value
    // on the price axis, exactly like TradingView's plot legend.
    { key: "bandHigh", label: "EMA Band High", color: "#26A69A", style: "line", lineWidth: 1, lineVisible: false, lastValueVisible: true, priceLineVisible: false },
    { key: "bandLow", label: "EMA Band Low", color: "#EF5350", style: "line", lineWidth: 1, lineVisible: false, lastValueVisible: true, priceLineVisible: false },
    { key: "trend", label: "Trend EMA", color: "#FF9800", style: "line", lineWidth: 2, lastValueVisible: true, priceLineVisible: false },
    { key: "ema50", label: "EMA 50", color: "#2962FF", style: "line", lineWidth: 1, lastValueVisible: false, priceLineVisible: false },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const len = candles.length;
    const bandLength = Math.max(2, Math.round(params.bandLength ?? 30));
    const trendLength = Math.max(2, Math.round(params.trendLength ?? 200));
    const ema50Length = Math.max(2, Math.round(params.ema50Length ?? 50));

    const bandFill = params.bandFill !== false;
    const fillOpacity = Math.max(0, Math.min(100, params.fillOpacity ?? 20)) / 100;
    const bullColor = params.bullColor ?? "#26A69A";
    const neutralColor = params.neutralColor ?? "#F0B90B";
    const bearColor = params.bearColor ?? "#EF5350";

    const useTrendFilter = params.useTrendFilter !== false;
    const showTrendLine = params.showTrendLine !== false;

    const confirmByClose = params.confirmByClose !== "hl";
    const showReversals = params.showReversals !== false;
    const reversalBars = Math.max(1, Number(params.reversalBars) || 2);
    const showShort = params.showShort !== false;
    // With SHORT signals disabled the alternation rule is lifted, otherwise a
    // LONG would never be allowed to print twice in a row.
    const alternation = showShort;
    const labelSize = Math.max(7, Number(params.labelSize) || 11);

    const showLevels = params.showLevels !== false;
    const peakColor = params.peakColor ?? "#26A69A";
    const troughColor = params.troughColor ?? "#EF5350";

    const showEma50 = params.showEma50 === true;
    const ema50Color = params.ema50Color ?? "#2962FF";

    if (len < bandLength + 2) {
      return [
        { key: "bandHigh", data: [] },
        { key: "bandLow", data: [] },
        { key: "trend", data: [] },
        { key: "ema50", data: [] },
      ];
    }

    // ── 1. Band + trend EMAs ────────────────────────────────────────────────
    const highs = candles.map(c => c.high);
    const lows = candles.map(c => c.low);
    const closes = candles.map(c => c.close);
    const emaHigh = computeEMA(highs, bandLength);
    const emaLow = computeEMA(lows, bandLength);
    const emaTrend = computeEMA(closes, trendLength);
    const ema50 = showEma50 ? computeEMA(closes, ema50Length) : null;

    // First bar with a fully seeded band.
    let start = bandLength - 1;
    while (start < len && !isFinite(emaHigh[start])) start++;

    const bandHighData: { time: number; value: number }[] = [];
    const bandLowData: { time: number; value: number }[] = [];
    const trendData: { time: number; value: number }[] = [];
    const ema50Data: { time: number; value: number }[] = [];
    for (let i = start; i < len; i++) {
      bandHighData.push({ time: candles[i].time, value: emaHigh[i] });
      bandLowData.push({ time: candles[i].time, value: emaLow[i] });
      if (showTrendLine && isFinite(emaTrend[i])) {
        trendData.push({ time: candles[i].time, value: emaTrend[i] });
      }
      if (ema50 && isFinite(ema50[i])) {
        ema50Data.push({ time: candles[i].time, value: ema50[i] });
      }
    }

    // ── 2. Band colouring: green above / yellow inside / red below ──────────
    const zoneOf: number[] = new Array(len);
    for (let i = 0; i < len; i++) {
      if (!isFinite(emaHigh[i]) || !isFinite(emaLow[i])) { zoneOf[i] = -1; continue; }
      const c = candles[i].close;
      zoneOf[i] = c > emaHigh[i] ? ZONE_BULL : c < emaLow[i] ? ZONE_BEAR : ZONE_NEUTRAL;
    }

    const zoneColors = [
      withAlpha(bullColor, fillOpacity),
      withAlpha(neutralColor, fillOpacity),
      withAlpha(bearColor, fillOpacity),
    ];
    const zoneBorders = [shade(bullColor, 0.8), shade(neutralColor, 0.8), shade(bearColor, 0.8)];

    const barStep = len >= 2 ? Math.max(1, candles[1].time - candles[0].time) : 0;

    // Collapse consecutive bars of the same zone into one painted segment.
    const bandSegments: NonNullable<IndicatorCurveBand["segments"]> = [];
    let segStart = -1;
    for (let i = start; i <= len; i++) {
      const zone = i < len ? zoneOf[i] : -2; // -2 forces the trailing run to close
      if (segStart >= 0 && zone !== zoneOf[segStart]) {
        const endIdx = i - 1;
        bandSegments.push({
          fromTime: candles[segStart].time,
          toTime:
            endIdx + 1 < len
              ? candles[endIdx + 1].time
              : candles[endIdx].time + barStep,
          color: zoneColors[zoneOf[segStart]],
          borderColor: zoneBorders[zoneOf[segStart]],
        });
        segStart = -1;
      }
      if (i < len && zone >= 0 && segStart < 0) segStart = i;
    }

    const curveBands: IndicatorCurveBand[] =
      bandSegments.length > 0
        ? [
            {
              id: "emabb-band",
              upper: bandHighData,
              lower: bandLowData,
              segments: bandSegments,
              borderWidth: 1,
              fill: bandFill,
            },
          ]
        : [];

    // ── 3. Signal state machine (closed bars only) ──────────────────────────
    const lastClosedBar = len - 2;
    const labels: IndicatorChartLabel[] = [];
    const segments: IndicatorLineSegment[] = [];

    let aboveRun = false;      // currently inside an excursion above the band
    let belowRun = false;      // currently inside an excursion below the band
    let excursionPeak = -Infinity;
    let excursionTrough = Infinity;

    let prevPeak = -Infinity;  // stored reference high
    let prevTrough = Infinity; // stored reference low
    let peakFrom = 0;          // first bar of the excursion that produced prevPeak
    let troughFrom = 0;        // first bar of the excursion that produced prevTrough

    // Live dotted level lines, retired whenever a new swing replaces them.
    let openPeak: IndicatorLineSegment | null = null;
    let openTrough: IndicatorLineSegment | null = null;

    let lastSignal = SIG_NONE;
    let belowCloses = 0;       // consecutive closes below the EMA band low
    let aboveCloses = 0;       // consecutive closes above the EMA band high

    for (let i = start; i <= lastClosedBar; i++) {
      const c = candles[i];
      const close = c.close;
      const eh = emaHigh[i];
      const el = emaLow[i];
      const et = emaTrend[i];

      const aboveBand = close > eh;
      const belowBand = close < el;

      // The trend filter is bypassed while the trend EMA is still warming up
      // (fewer bars than its own period) so short histories are not silenced.
      const trendKnown = useTrendFilter && isFinite(et);
      const trendOkLong = !trendKnown || close > et;
      const trendOkShort = !trendKnown || close < et;

      belowCloses = belowBand ? belowCloses + 1 : 0;
      aboveCloses = aboveBand ? aboveCloses + 1 : 0;

      // ── Track the excursion beyond the band, store the swing on return ──
      if (aboveBand) {
        if (!aboveRun) { aboveRun = true; peakFrom = i; excursionPeak = -Infinity; }
        excursionPeak = Math.max(excursionPeak, c.high);
      } else if (aboveRun) {
        aboveRun = false;
        prevPeak = excursionPeak;
        excursionPeak = -Infinity;
      }

      if (belowBand) {
        if (!belowRun) { belowRun = true; troughFrom = i; excursionTrough = Infinity; }
        excursionTrough = Math.min(excursionTrough, c.low);
      } else if (belowRun) {
        belowRun = false;
        prevTrough = excursionTrough;
        excursionTrough = Infinity;
      }

      // ── Breakout signals ────────────────────────────────────────────────
      const brokePeak =
        aboveBand &&
        isFinite(prevPeak) &&
        (confirmByClose ? close > prevPeak : c.high > prevPeak);
      const brokeTrough =
        belowBand &&
        isFinite(prevTrough) &&
        (confirmByClose ? close < prevTrough : c.low < prevTrough);

      let signal = SIG_NONE;
      if (brokePeak && trendOkLong && (!alternation || lastSignal !== SIG_LONG)) {
        signal = SIG_LONG;
      } else if (
        showShort &&
        brokeTrough &&
        trendOkShort &&
        (!alternation || lastSignal !== SIG_SHORT)
      ) {
        signal = SIG_SHORT;
      }

      // ── Reversal signals (catch one-way moves that never break a swing) ──
      if (
        signal === SIG_NONE &&
        showReversals &&
        showShort &&
        lastSignal === SIG_LONG &&
        belowCloses >= reversalBars &&
        trendOkShort
      ) {
        signal = SIG_SHORT;
      } else if (
        signal === SIG_NONE &&
        showReversals &&
        showShort &&
        lastSignal === SIG_SHORT &&
        aboveCloses >= reversalBars &&
        trendOkLong
      ) {
        signal = SIG_LONG;
      }

      if (signal !== SIG_NONE) {
        lastSignal = signal;
        const isLong = signal === SIG_LONG;
        labels.push({
          time: c.time,
          price: isLong ? c.low : c.high,
          text: isLong ? "LONG" : "SHORT",
          color: isLong ? LONG_COLOR : SHORT_COLOR,
          textColor: "#FFFFFF",
          position: isLong ? "belowBar" : "aboveBar",
          size: labelSize,
          offset: Math.max(6, labelSize * 0.7),
        });
      }

      // ── Reference level lines (dotted) ──────────────────────────────────
      if (showLevels) {
        if (openPeak && openPeak.price !== prevPeak) {
          openPeak.toTime = c.time;
          openPeak = null;
        }
        if (!openPeak && isFinite(prevPeak)) {
          openPeak = {
            id: `emabb-peak-${i}`,
            price: prevPeak,
            fromTime: candles[Math.max(start, peakFrom)].time,
            toTime: c.time,
            color: withAlpha(peakColor, 0.85),
            lineStyle: 1,
            lineWidth: 1,
          };
          segments.push(openPeak);
        } else if (openPeak) {
          openPeak.toTime = c.time;
        }

        if (openTrough && openTrough.price !== prevTrough) {
          openTrough.toTime = c.time;
          openTrough = null;
        }
        if (!openTrough && isFinite(prevTrough)) {
          openTrough = {
            id: `emabb-trough-${i}`,
            price: prevTrough,
            fromTime: candles[Math.max(start, troughFrom)].time,
            toTime: c.time,
            color: withAlpha(troughColor, 0.85),
            lineStyle: 1,
            lineWidth: 1,
          };
          segments.push(openTrough);
        } else if (openTrough) {
          openTrough.toTime = c.time;
        }
      }
    }

    return [
      {
        key: "bandHigh",
        data: bandHighData,
        curveBands,
        segments,
        labels,
      },
      { key: "bandLow", data: bandLowData },
      { key: "trend", data: trendData },
      { key: "ema50", data: ema50Data },
    ];
  },
};